import { useRef, useState, useEffect } from "react";
import { Col, Row, Button, Input, Select, DatePicker, message } from "antd";
import CustomButton from "@/components/CustomButton";
import { CloseOutlined, LoadingOutlined } from "@ant-design/icons";
import { useHistory, useParams, useRouteMatch } from "react-router-dom";

import CountryPhoneInput, { ConfigProvider } from "antd-country-phone-input";
import en from "../../../constants/world.json";

import moment from "moment";

import "./createCompany.less";
import ImgUpload from "@assets/images/img-upload.png";
import {
  disabledDates,
  generateArrayOfYears,
  showSuccessPopup,
  sortCountriesAlphabetically,
  sortCurrenciesAlphabetically,
  getSortedIndustryNames,
} from "@utils";
import { useTypedSelector } from "@hooks";
import {
  ErrorServices,
  useCreateCompanyMutation,
  useFetchCompanyQuery,
  useFetchCurrenciesQuery,
  useUpdateCompanyMutation,
  useUploadCompanyImageMutation,
} from "@services";
import { ICurrency } from "@types";
import { paths } from "@router";
import customErrorMessage from "@/components/CustomErrorMessage";

const { Option } = Select;

function beforeUpload(file: any) {
  const isJpgOrPng = file.type === "image/jpeg" || file.type === "image/png";
  if (!isJpgOrPng) {
    customErrorMessage("You can only upload JPG/PNG file!");
  }
  const isLt2M = file.size / 1024 / 1024 < 2;
  if (!isLt2M) {
    customErrorMessage("Image must be smaller than 2MB!");
  }
  return isJpgOrPng && isLt2M;
}

interface IFormValues {
  country_id: number | null;
  currency_id: number | null;
  name: string;
  address: string;
  postal_code: string;
  country_headquarter: string;
  financial_year: number | null;
  contract_start_date: string;
  contract_end_date: string;
  large_logo_url: string;
  small_logo_url: string;
  stock_data: any[];

  // stock_tracking_ids: string[];
  person_name: string;
  person_phone_code: number | null;
  person_phone_number: string;
  person_email: string;
  person_country_id: number | null;
  application_access: boolean;
  status?: string;
  stock_tracking_ids?: [];
  applications?: string[];
}

const INITIAL_VALUES: IFormValues = {
  country_id: null,
  currency_id: null,
  name: "",
  address: "",
  postal_code: "",
  country_headquarter: "",
  financial_year: null,
  contract_end_date: "",
  contract_start_date: "",
  small_logo_url:
    "https://cdn.sanity.io/images/92ui5egz/production/7c1c60e9afaaaa3cce61e5101717796d21b7f914-1426x1080.svg?auto=format",
  large_logo_url:
    "https://cdn.sanity.io/images/92ui5egz/production/7c1c60e9afaaaa3cce61e5101717796d21b7f914-1426x1080.svg?auto=format",
  // stock_tracking_ids: [],
  stock_data: [],
  person_name: "",
  person_phone_code: null,
  person_phone_number: "",
  person_email: "",
  person_country_id: null,
  application_access: false,
  applications: ["TOM", "JE", "LIVE_PAY_BENCHMARKING_TOOL"],
};

type IFormKeys = keyof IFormValues;

const validateValues = (data: IFormValues) => {
  console.log("1", data);
  let cond = false;
  for (var key in data) {
    if (data[key as IFormKeys] === "" || data[key as IFormKeys] === null) {
      cond = true;
    }
  }
  return cond;
};

const AdminCreateCompany = () => {
  const history = useHistory();
  const inputRef = useRef<any>();
  const { company_id } = useParams<{ company_id: string }>();
  const [largeLogo, setLargeLogo] = useState("");
  const [loadingLargeLogo, setLoadingLargeLogo] = useState(false);
  const { path } = useRouteMatch<{ path: string }>();
  const [stockId, setStockId] = useState("");
  const [status, setStatus] = useState("ACTIVE");
  const [isDisabled, setIsDisabled] = useState(true);
  const { countries } = useTypedSelector(state => state.countries);
  const { data: currenciesData, isLoading: isFetchingCurrencies } =
    useFetchCurrenciesQuery(null);
  const [createCompany, { isLoading: isCreating }] = useCreateCompanyMutation();

  const { data: currencies } = currenciesData || {};
  const [company, setCompany] = useState(INITIAL_VALUES);
  const isEdit = path === paths.client.users.companies.edit;
  const [uploadCompanyImage] = useUploadCompanyImageMutation();
  const { data: companyData } = useFetchCompanyQuery({ company_id });
  const { data: selectedCompany } = companyData || {};
  const [updateCompany, { isLoading: isUpdating }] = useUpdateCompanyMutation();

  function disabledDate(current: any) {
    return disabledDates(
      current,
      moment(moment(company?.contract_start_date)).endOf("day")
    );
  }

  useEffect(() => {
    if (isEdit && selectedCompany) {
      const { country, currency, user, person_country, stock_data } =
        selectedCompany;
      const company = {
        ...selectedCompany,
        country_id: country?.id,
        currency_id: currency?.id,
        person_country_id: person_country?.id,
        person_email: user?.email,
        person_name: user?.first_name + user?.last_name,
        person_phone_code: user?.phone_code,
        person_phone_number: user?.phone_number,
        stock_data,
      };
      delete company?.country;
      delete company?.currency;
      delete company?.user;
      delete company?.person_country;
      delete company?.applications;
      setStatus(company?.status);
      setLargeLogo(selectedCompany?.large_logo_url);
      setCompany(company);
    }
  }, [selectedCompany, path, isEdit]);

  const handleImage = async (event: any) => {
    setLoadingLargeLogo(true);
    const file = event.target.files[0];
    const isCond = beforeUpload(file);
    if (isCond) {
      try {
        const formData = new FormData();
        formData.append("file", file);
        const res = await uploadCompanyImage({
          body: formData,
        }).unwrap();
        setLargeLogo(res?.data?.url);
        message.success(res?.message);
      } catch (err: any) {
        // customErrorMessage(error?.message);
        // console.log(error);
      }
    }
  };

  const onChange = (event: any) => {
    setCompany(prev => ({
      ...prev,
      [event.target.name]: event.target.value,
    }));
  };

  const onSubmit = async () => {
    try {
      if (isEdit) {
        await editCompany({ ...company, large_logo_url: largeLogo, status });
      } else {
        await addCompany({
          ...company,
          status,
          large_logo_url: largeLogo || INITIAL_VALUES.large_logo_url,
          stock_tracking_ids: [],
          // applications: ["TOM"],
          applications: ["TOM", "JE", "LIVE_PAY_BENCHMARKING_TOOL"],
        });
      }
      history.push(paths.admin.users.companies.listing);
      showSuccessPopup({
        title: selectedCompany ? "Company Updated" : "New Company Created",
        desc: `You have successfully ${
          selectedCompany ? "updated the" : "created new"
        } company.`,
      });
    } catch (err: any) {
      ErrorServices(err);
      console.log(err);
    }
  };

  const addCompany = async (body: IFormValues) =>
    await createCompany({ body }).unwrap();

  const editCompany = async (body: any) =>
    await updateCompany({ body, company_id }).unwrap();

  return (
    <>
      {/* <div className="container">
        <Row>
          <Col span={24}>
            <div className="main-heading mb-16">{`${isEdit ? "Update the" : "Create New"
              } Company`}</div>
          </Col>
        </Row>
        <div className="addCompany">
          <Row className="addCompany__header">
            <div className="sub-heading">Basic information</div>
          </Row>
          <div className="addCompany__content">
            <Row className="addCompany__row" justify="space-between">
              <Col span={9}>
                <Row>
                 
                  <label className="input__label">
                    Company Name{" "}
                    <span className="text-red-500 text-base">
                      {company?.name ? "" : "*"}
                    </span>
                  </label>

                  <Input
className="custom-react-select borderless-input"
className="custom-react-select"
                
                    onChange={onChange}
                    name="name"
                    value={company?.name}
                    size="large"
                    type="text"
                    placeholder="Enter company name here..."
                  />
                </Row>
                <Row>
                  
                  <label className="mt-12">
                    Address{" "}
                    <span className="text-red-500 text-base">
                      {company?.address ? "" : "*"}
                    </span>
                  </label>

                  <Input
className="custom-react-select borderless-input"
className="custom-react-select"
                
                    onChange={onChange}
                    value={company?.address}
                    name="address"
                    size="large"
                    type="text"
                    placeholder="Enter company address here..."
                  />
                </Row>
                <Row>
              
                  <label className="mt-12">
                    Postal Code{" "}
                    <span className="text-red-500 text-base">
                      {company?.postal_code ? "" : "*"}
                    </span>
                  </label>

                  <Input
className="custom-react-select borderless-input"
className="custom-react-select"
                
                    value={company?.postal_code}
                    onChange={onChange}
                    name="postal_code"
                    size="large"
                    type="text"
                    placeholder="Enter postal code here..."
                  />
                </Row>
                <label className="mt-12">
                  Status{" "}
                  <span className="text-red-500 text-base">
                    {status ? "" : "*"}
                  </span>
                </label>

                <Select
className="custom-react-select"
                
                  disabled={!selectedCompany}
                  onChange={status => setStatus(status)}
                  value={status}
                  showArrow={true}
                  size="large"
                  className="mb-12"
                  placeholder="Select status from here..."
                >
                  {[
                    { value: "IN_PROGRESS", label: "In Progress" },
                    { value: "ACTIVE", label: "Active" },
                    { value: "EXPIRED", label: "Expired" },
                  ]?.map(({ value, label }) => (
                    <Option key={value} value={value}>
                      {label}
                    </Option>
                  ))}
                </Select>
              </Col>
              <Col className="addCompany__col2" span={9}>
                <Input
className="custom-react-select borderless-input"
className="custom-react-select"
                
                  id="myInput"
                  type="file"
                  ref={inputRef}
                  hidden={true}
                  accept="image/png, image/jpeg"
                  onChange={handleImage}
                />
                <div onClick={() => inputRef?.current?.click()}>
                  <div className="addCompany__col2__image">
                    {loadingLargeLogo && <LoadingOutlined className="spinner" />}

                    <img
                      className={`${loadingLargeLogo ? "addCompany__col2__image--hidden" : ""
                        }`}
                      onLoadCapture={() => {
                        setLoadingLargeLogo(false);
                      }}
                      src={largeLogo || ImgUpload}
                      alt="avatar"
                    />
                  </div>
                  <p className="img_description_text">
                    UPLOAD LARGE LOGO{" "}
                    <span className="text-red-500 text-base">
                      {largeLogo ? "" : "*"}
                    </span>
                  </p>

                  <p className="img_description_size">1000 x 1000</p>
                </div>
              </Col>
            </Row>
            <div className="mt-24 mb-12 sub-heading">Corporate Information</div>
            <Row className="addCompany__row" justify="space-between">
              <Col span={9}>
                <Row>
               
                  <label className="input__label">
                    Country{" "}
                    <span className="text-red-500 text-base">
                      {company?.country_id ? "" : "*"}
                    </span>
                  </label>
                  <Select
className="custom-react-select"
                
                    onChange={country_id => {
                      setCompany(prev => ({
                        ...prev,
                        country_id,
                      }));
                    }}
                    value={company?.country_id || undefined}
                    showArrow={true}
                    size="large"
                    showSearch={true}
                    filterOption={(input: any, option: any) =>
                      String(option?.children ?? "")
                        .toLowerCase()
                        .indexOf(input.toLowerCase()) >= 0
                    }
                    placeholder="Select country from here..."
                  >
                    {sortCountriesAlphabetically(countries)?.map(
                      ({ id, name }) => (
                        <Option key={id} value={id}>
                          {name}
                        </Option>
                      )
                    )}
                  </Select>
                </Row>
                <Row>
                  
                  <label className="mt-12">
                    Headquarter Location{" "}
                    <span className="text-red-500 text-base">
                      {company?.country_headquarter ? "" : "*"}
                    </span>
                  </label>

                  <Input
className="custom-react-select borderless-input"
className="custom-react-select"
                
                    value={company?.country_headquarter}
                    name="country_headquarter"
                    onChange={onChange}
                    className="form__input"
                    type="text"
                    placeholder="Enter location of headquater"
                  />
                </Row>
                <Row>
             
                  <label className="mt-12">
                    Currency{" "}
                    <span className="text-red-500 text-base">
                      {company?.currency_id ? "" : "*"}
                    </span>
                  </label>
                  <Select
className="custom-react-select"
                
                    value={company?.currency_id || undefined}
                    loading={isFetchingCurrencies}
                    onChange={currency_id =>
                      setCompany(prev => ({
                        ...prev,
                        currency_id,
                      }))
                    }
                    showArrow={true}
                    showSearch={true}
                    size="large"
                    filterOption={(input: any, option: any) =>
                      String(option?.children ?? "")
                        .toLowerCase()
                        .indexOf(input.toLowerCase()) >= 0
                    }
                    placeholder="Select currency from here..."
                  >
                    {sortCurrenciesAlphabetically(currencies)?.map(
                      ({ id, name, code }: ICurrency) => (
                        <Option key={id} value={id}>
                          {`${name} (${code})`}
                        </Option>
                      )
                    )}
                  </Select>
                </Row>
                <Row>
           
                  <label className="mt-12">
                    Financial Year{" "}
                    <span className="text-red-500 text-base">
                      {company?.financial_year ? "" : "*"}
                    </span>
                  </label>

                  <Select
className="custom-react-select"
                
                    onChange={financial_year =>
                      setCompany(prev => ({ ...prev, financial_year }))
                    }
                    value={company?.financial_year || undefined}
                    filterOption={(input: any, option: any) =>
                      String(option?.children ?? "")
                        .toString()
                        .toLowerCase()
                        .indexOf(input.toString().toLowerCase()) >= 0
                    }
                    showSearch={true}
                    size="large"
                    placeholder="Select financial year from here..."
                  >
                    {generateArrayOfYears(50)?.map((year: number) => (
                      <Option key={year.toString()} value={year.toString()}>
                        {year}
                      </Option>
                    ))}
                  </Select>
                </Row>
              </Col>
              <Col className="addCompany__col2" span={9} />
            </Row>

            <Row>
              <Col span={23}>
                <Row>
                  <div className="flex  gap-2 flex-wrap  justify-start items-center mt-12">
                    {company?.stock_data && company?.stock_data.length > 0
                      ? company?.stock_data?.map((item, i) => {
                        return (
                          <div key={i} className="bg-gray-100 p-4 rounded-md">
                            <div className="flex justify-end items-end">
                              <CloseOutlined
                                className="right-0 cursor-pointer"
                                onClick={() => {
                                  setCompany(prev => ({
                                    ...prev,
                                    stock_data: prev.stock_data.filter(
                                      x => x.tracking_id !== item?.tracking_id
                                    ),
                                  }));
                                }}
                              />
                            </div>

                            <div>
                              <label className="">Stock Tracking ID</label>
                              <Input
className="custom-react-select borderless-input"
className="custom-react-select"
                
                                size="large"
                                value={item?.tracking_id}
                                onChange={e => {
                                  const updatedTrackingId = e.target.value;
                                  setCompany(prev => ({
                                    ...prev,
                                    stock_data: prev.stock_data.map((x, idx) =>
                                      idx === i
                                        ? {
                                          ...x,
                                          tracking_id: updatedTrackingId,
                                        }
                                        : x
                                    ),
                                  }));
                                }}
                                type="text"
                                placeholder="Enter stock tracking ID here..."
                              />
                            </div>
                            <div>
                              <label className="">Stock Type</label>
                              <Select
className="custom-react-select"
                
                                onChange={stockType =>
                                  setCompany(prev => ({
                                    ...prev,
                                    stock_data: prev.stock_data.map(
                                      (item, idx) =>
                                        idx === i
                                          ? { ...item, stock_type: stockType }
                                          : item
                                    ),
                                  }))
                                }
                                value={item?.stock_type || undefined}
                                showSearch={true}
                                size="large"
                                placeholder="Select stock type from here..."
                              >
                                <Option value="in-House">In-House</Option>
                                <Option value="international">
                                  International
                                </Option>
                              </Select>
                            </div>
                            <div>
                              <label className="mt-">Currency</label>
                              <Select
className="custom-react-select"
                
                                value={item?.stock_currency_id || undefined}
                                loading={isFetchingCurrencies}
                                onChange={currency_id =>
                                  setCompany(prev => ({
                                    ...prev,
                                    stock_data: prev.stock_data.map(
                                      (item, idx) =>
                                        idx === i
                                          ? {
                                            ...item,
                                            stock_currency_id: currency_id,
                                          }
                                          : item
                                    ),
                                  }))
                                }
                                showArrow={true}
                                showSearch={true}
                                size="large"
                                filterOption={(input: any, option: any) =>
                                  String(option?.children ?? "")
                                    .toLowerCase()
                                    .indexOf(input.toLowerCase()) >= 0
                                }
                                placeholder="Select currency from here..."
                              >
                               

                                {item?.stock_type === "international" ? (
                                  <Option key={138} value={138}>
                                    USD ($)
                                  </Option>
                                ) : (
                                  currencies?.map(
                                    ({ id, name, symbol }: ICurrency) => (
                                      <Option key={id} value={id}>
                                        {`${name} (${symbol})`}
                                      </Option>
                                    )
                                  )
                                )}
                              </Select>
                            </div>
                            <div>
                              <label className="">Stock Value</label>
                              <Input
className="custom-react-select borderless-input"
className="custom-react-select"
                
                                size="large"
                                contentEditable={
                                  item?.stock_type != "international"
                                }
                                disabled={item?.stock_type === "international"}
                                value={item?.stock_value}
                                onChange={e => {
                                  const updatedStockValue = e.target.value;
                                  setCompany(prev => ({
                                    ...prev,
                                    stock_data: prev.stock_data.map((x, idx) =>
                                      idx === i
                                        ? {
                                          ...x,
                                          stock_value: updatedStockValue,
                                        }
                                        : x
                                    ),
                                  }));
                                }}
                                type="number"
                                placeholder="Enter stock value here..."
                              />
                            </div>
                          </div>
                        );
                      })
                      : null}

                    <div
                      className="bg-gray-100 p-4 rounded-md flex justify-center h-8 items-center cursor-pointer"
                      onClick={() => {
                        setCompany(prev => ({
                          ...prev,
                          stock_data: [
                            ...prev.stock_data,
                            {
                              tracking_id: "",
                              stock_type: "",
                              stock_value: "",
                            },
                          ],
                        }));
                      }}
                    >
                      <span className="text-sm font-bold">Add Stock</span>
                    </div>
                  </div>
                </Row>
              </Col>
            </Row>
            <div className="mt-24 mb-12 sub-heading">Contract Details</div>
          
            <label className="input__label">
              Contract Duration{" "}
              <span className="text-red-500 text-base">
                {company?.contract_start_date && company?.contract_end_date
                  ? ""
                  : "*"}
              </span>
            </label>

            <Row className="addCompany__row" justify="space-between">
              <Col span={12}>
                <Row justify="space-between">
                  <Col span={11}>
                    <DatePicker
                      className="width-100"
                      value={
                        !company?.contract_start_date
                          ? undefined
                          : moment(company?.contract_start_date)
                      }
                      onChange={date => {
                        setCompany(prev => ({
                          ...prev,
                          contract_start_date: moment(date)
                            .format("YYYY-MM-DD HH:mm:ss")
                            ?.toString() as string,
                        }));
                        setIsDisabled(false);
                      }}
                      placeholder="Starting Date (DD/MM/YY)"
                    />
                  </Col>
                  <Col className="mb-12" span={11}>
                    <DatePicker
                      disabled={isDisabled}
                      disabledDate={disabledDate}
                      className="width-100"
                      value={
                        !company?.contract_end_date
                          ? undefined
                          : moment(company?.contract_end_date)
                      }
                      onChange={date =>
                        setCompany(prev => ({
                          ...prev,
                          contract_end_date: moment(date)
                            .format("YYYY-MM-DD HH:mm:ss")
                            ?.toString() as string,
                        }))
                      }
                      placeholder="Ending Date (DD/MM/YY)"
                    />
                  </Col>
                </Row>
              </Col>
              <Col span={12}></Col>
            </Row>

            <div className="mt-24 mb-12 sub-heading">Contact Person Details</div>
            <Row className="addCompany__row" justify="space-between">
              <Col span={11}>
                <Row>
                  <label className="input__label">
                    Contact Person{" "}
                    <span className="text-red-500 text-base">
                      {company?.person_name ? "" : "*"}
                    </span>
                  </label>

                 
                  <Input
className="custom-react-select borderless-input"
className="custom-react-select"
                
                    size="large"
                    name="person_name"
                    onChange={onChange}
                    value={company?.person_name}
                    placeholder="Enter name of contact person..."
                  />
                </Row>
                
                <label className="mt-12">
                  Contact Number{" "}
                  <span className="text-red-500 text-base">
                    {company?.person_phone_code && company?.person_phone_number
                      ? ""
                      : "*"}
                  </span>
                </label>

                <Row className="addCompany__number">
                  <ConfigProvider locale={en}>
                    <CountryPhoneInput
                      placeholder="Enter contact number here..."
                      onChange={e => {
                        setCompany(prev => ({
                          ...prev,
                          person_phone_code: e.code as number,
                          person_phone_number: e.phone as string,
                        }));
                      }}
                      value={{
                        phone: company?.person_phone_number,
                        code: company?.person_phone_code || 92,
                      }}
                    />
                  </ConfigProvider>
                </Row>
              </Col>
              <Col span={11}>
                <Row>
                  
                  <label className="input__label">
                    Contact Person's Country{" "}
                    <span className="text-red-500 text-base">
                      {company?.person_country_id ? "" : "*"}
                    </span>
                  </label>

                  <Select
className="custom-react-select"
                
                    onChange={person_country_id => {
                      setCompany(prev => ({
                        ...prev,
                        person_country_id,
                      }));
                    }}
                    value={company?.person_country_id || undefined}
                    showArrow={true}
                    showSearch={true}
                    size="large"
                    filterOption={(input: any, option: any) =>
                      String(option?.children ?? "")
                        .toLowerCase()
                        .indexOf(input.toLowerCase()) >= 0
                    }
                    placeholder="Select country from here..."
                  >
                    {countries?.map(({ id, name }) => (
                      <Option key={"" + id} value={id}>
                        {name}
                      </Option>
                    ))}
                  </Select>
                </Row>
                <Row className="mt-12 mb-12">
              
                  <label className="input__label">
                    Email Address{" "}
                    <span className="text-red-500 text-base">
                      {company?.person_email ? "" : "*"}
                    </span>
                  </label>

                  <Input
className="custom-react-select borderless-input"
className="custom-react-select"
                
                    name="person_email"
                    value={company?.person_email}
                    onChange={onChange}
                    size="large"
                    placeholder="Enter email address here..."
                  />
                </Row>
              </Col>
            </Row>
            <Row className="addCompany__btns">
              <Col span={24} className="flex items-center">
                <CustomButton
                
                  btnName={
                    isCreating || isUpdating ? (
                      <LoadingOutlined className="spinner" />
                    ) : (
                      `${isEdit ? "Update" : "Create"} Company`
                    )
                  }
                  disabledProp={validateValues(company) || !largeLogo}
                  
                  onClick={onSubmit}
                >
                  {isCreating || isUpdating ? (
                    <LoadingOutlined className="spinner" />
                  ) : (
                    `${isEdit ? "Update" : "Create"} Company`
                  )}
                </CustomButton>

                <CustomButton
                  btnName={"Cancel"}
                  
                  onClick={() =>
                    history.push(
                      paths.client.users.companies.profile.details.replace(
                        ":company_id",
                        company_id
                      )
                    )
                  }
                >
                  Cancel
                </CustomButton>
              </Col>
            </Row>
          </div>
        </div>
      </div> */}

      <div className="container" style={{ height: "35rem" }}>
        <div className="bg-blue-100 rounded-lg p-5 ">
          {/* basic info */}
          <div className="basicInfo mb-10">
            <h2 className="text-center text-gray-700 font-medium text-lg mb-8">
              Basic Information
            </h2>

            <Row gutter={32} align="top">
              {/* Left Side */}
              <Col span={18}>
                <Row gutter={[24, 24]}>
                  {/* Company Name */}
                  <Col span={12}>
                    <label className="input__label">
                      Company Name{" "}
                      <span className="text-red-500">
                        {company?.name ? "" : "*"}
                      </span>
                    </label>

                    <Input
                      className="custom-react-select borderless-input"
                      onChange={onChange}
                      name="name"
                      value={company?.name}
                      size="large"
                      placeholder="Enter company name here..."
                    />
                  </Col>

                  {/* Address */}
                  <Col span={12}>
                    <label className="input__label">
                      Address{" "}
                      <span className="text-red-500">
                        {company?.address ? "" : "*"}
                      </span>
                    </label>

                    <Input
                      className="custom-react-select borderless-input"
                      onChange={onChange}
                      name="address"
                      value={company?.address}
                      size="large"
                      placeholder="Enter company address here..."
                    />
                  </Col>

                  {/* Postal Code */}
                  <Col span={12}>
                    <label className="input__label">
                      Postal Code{" "}
                      <span className="text-red-500">
                        {company?.postal_code ? "" : "*"}
                      </span>
                    </label>

                    <Input
                      className="custom-react-select borderless-input"
                      onChange={onChange}
                      name="postal_code"
                      value={company?.postal_code}
                      size="large"
                      placeholder="Enter postal code here..."
                    />
                  </Col>

                  {/* Status */}
                  <Col span={12}>
                    <label className="input__label">
                      Status{" "}
                      <span className="text-red-500">{status ? "" : "*"}</span>
                    </label>

                    <Select
                      className="custom-react-select"
                      disabled={!selectedCompany}
                      value={status}
                      onChange={setStatus}
                      size="large"
                      placeholder="Select status..."
                      style={{ width: "100%" }}
                    >
                      <Option value="IN_PROGRESS">In Progress</Option>
                      <Option value="ACTIVE">Active</Option>
                      <Option value="EXPIRED">Expired</Option>
                    </Select>
                  </Col>
                </Row>
              </Col>

              {/* Right Side */}
              <Col span={6} className="flex justify-center">
                <div className="text-center">
                  <Input
                    className="custom-react-select borderless-input"
                    id="myInput"
                    type="file"
                    ref={inputRef}
                    hidden
                    accept="image/png, image/jpeg"
                    onChange={handleImage}
                  />

                  <div onClick={() => inputRef.current?.click()}>
                    <div className="addCompany__col2__image">
                      {loadingLargeLogo && (
                        <LoadingOutlined className="spinner" />
                      )}

                      <img
                        className={
                          loadingLargeLogo
                            ? "addCompany__col2__image--hidden"
                            : ""
                        }
                        onLoad={() => setLoadingLargeLogo(false)}
                        src={largeLogo || ImgUpload}
                        alt="logo"
                      />
                    </div>

                    <p className="img_description_text">
                      UPLOAD LARGE LOGO{" "}
                      <span className="text-red-500">
                        {largeLogo ? "" : "*"}
                      </span>
                    </p>

                    <p className="img_description_size">1000 × 1000</p>
                  </div>
                </div>
              </Col>
            </Row>
          </div>

          {/* corporate info */}
          <div className="basicInfo mb-10">
            <h2 className="text-center text-gray-700 font-medium text-lg mb-8">
              Corporate Information
            </h2>

            <Row className="addCompany__row" gutter={[24, 24]}>
              <Col span={18}>
                <Row gutter={[24, 24]}>
                  {/* Country */}
                  <Col span={12}>
                    <label className="input__label">
                      Country{" "}
                      <span className="text-red-500">
                        {company?.country_id ? "" : "*"}
                      </span>
                    </label>

                    <Select
                      className="custom-react-select"
                      value={company?.country_id || undefined}
                      onChange={country_id =>
                        setCompany(prev => ({
                          ...prev,
                          country_id,
                        }))
                      }
                      size="large"
                      showSearch
                      showArrow
                      placeholder="Select country from here..."
                      filterOption={(input, option) =>
                        String(option?.children ?? "")
                          .toLowerCase()
                          .includes(input.toLowerCase())
                      }
                    >
                      {sortCountriesAlphabetically(countries).map(
                        ({ id, name }) => (
                          <Option key={id} value={id}>
                            {name}
                          </Option>
                        )
                      )}
                    </Select>
                  </Col>

                  {/* Headquarter */}
                  <Col span={12}>
                    <label className="input__label">
                      Headquarter Location{" "}
                      <span className="text-red-500">
                        {company?.country_headquarter ? "" : "*"}
                      </span>
                    </label>

                    <Input
                      className="custom-react-select borderless-input"
                      value={company?.country_headquarter}
                      name="country_headquarter"
                      onChange={onChange}
                      size="large"
                      placeholder="Enter location of headquarter"
                    />
                  </Col>

                  {/* Currency */}
                  <Col span={12}>
                    <label className="input__label">
                      Currency{" "}
                      <span className="text-red-500">
                        {company?.currency_id ? "" : "*"}
                      </span>
                    </label>

                    <Select
                      className="custom-react-select"
                      value={company?.currency_id || undefined}
                      loading={isFetchingCurrencies}
                      onChange={currency_id =>
                        setCompany(prev => ({
                          ...prev,
                          currency_id,
                        }))
                      }
                      size="large"
                      showSearch
                      showArrow
                      placeholder="Select currency from here..."
                      filterOption={(input, option) =>
                        String(option?.children ?? "")
                          .toLowerCase()
                          .includes(input.toLowerCase())
                      }
                    >
                      {sortCurrenciesAlphabetically(currencies).map(
                        ({ id, name, code }: ICurrency) => (
                          <Option key={id} value={id}>
                            {`${name} (${code})`}
                          </Option>
                        )
                      )}
                    </Select>
                  </Col>

                  {/* Financial Year */}
                  <Col span={12}>
                    <label className="input__label">
                      Financial Year{" "}
                      <span className="text-red-500">
                        {company?.financial_year ? "" : "*"}
                      </span>
                    </label>

                    <Select
                      className="custom-react-select"
                      value={company?.financial_year || undefined}
                      onChange={financial_year =>
                        setCompany(prev => ({
                          ...prev,
                          financial_year,
                        }))
                      }
                      size="large"
                      showSearch
                      placeholder="Select financial year from here..."
                      filterOption={(input, option) =>
                        String(option?.children ?? "")
                          .toString()
                          .toLowerCase()
                          .includes(input.toLowerCase())
                      }
                    >
                      {generateArrayOfYears(50).map(year => (
                        <Option key={year} value={year.toString()}>
                          {year}
                        </Option>
                      ))}
                    </Select>
                  </Col>
                </Row>
              </Col>

              {/* Empty right side to align with logo section above */}
              <Col
                span={6}
                className="flex flex-col items-center justify-center"
              >
                <div
                  className="bg-themeBlack text-white p-4 rounded-md flex justify-center h-8 items-center cursor-pointer"
                  onClick={() => {
                    setCompany(prev => ({
                      ...prev,
                      stock_data: [
                        ...prev.stock_data,
                        {
                          tracking_id: "",
                          stock_type: "",
                          stock_value: "",
                        },
                      ],
                    }));
                  }}
                >
                  <span className="text-sm font-bold">Add Stock</span>
                </div>
              </Col>
            </Row>

            <Row>
              <Col span={23}>
                <Row>
                  <div className="flex  gap-2 flex-wrap  justify-start items-center mt-12">
                    {company?.stock_data && company?.stock_data.length > 0
                      ? company?.stock_data?.map((item, i) => {
                          return (
                            <div key={i} className="bg-gray-100 p-4 rounded-md">
                              <div className="flex justify-end items-end">
                                <CloseOutlined
                                  className="right-0 cursor-pointer"
                                  onClick={() => {
                                    setCompany(prev => ({
                                      ...prev,
                                      stock_data: prev.stock_data.filter(
                                        x => x.tracking_id !== item?.tracking_id
                                      ),
                                    }));
                                  }}
                                />
                              </div>

                              <div>
                                <label className="">Stock Tracking ID</label>
                                <Input
                                  className="custom-react-select borderless-input"
                                  size="large"
                                  value={item?.tracking_id}
                                  onChange={e => {
                                    const updatedTrackingId = e.target.value;
                                    setCompany(prev => ({
                                      ...prev,
                                      stock_data: prev.stock_data.map(
                                        (x, idx) =>
                                          idx === i
                                            ? {
                                                ...x,
                                                tracking_id: updatedTrackingId,
                                              }
                                            : x
                                      ),
                                    }));
                                  }}
                                  type="text"
                                  placeholder="Enter stock tracking ID here..."
                                />
                              </div>
                              <div>
                                <label className="">Stock Type</label>
                                <Select
                                  className="custom-react-select"
                                  onChange={stockType =>
                                    setCompany(prev => ({
                                      ...prev,
                                      stock_data: prev.stock_data.map(
                                        (item, idx) =>
                                          idx === i
                                            ? { ...item, stock_type: stockType }
                                            : item
                                      ),
                                    }))
                                  }
                                  value={item?.stock_type || undefined}
                                  showSearch={true}
                                  size="large"
                                  placeholder="Select stock type from here..."
                                >
                                  <Option value="in-House">In-House</Option>
                                  <Option value="international">
                                    International
                                  </Option>
                                </Select>
                              </div>
                              <div>
                                <label className="mt-">Currency</label>
                                <Select
                                  className="custom-react-select"
                                  value={item?.stock_currency_id || undefined}
                                  loading={isFetchingCurrencies}
                                  onChange={currency_id =>
                                    setCompany(prev => ({
                                      ...prev,
                                      stock_data: prev.stock_data.map(
                                        (item, idx) =>
                                          idx === i
                                            ? {
                                                ...item,
                                                stock_currency_id: currency_id,
                                              }
                                            : item
                                      ),
                                    }))
                                  }
                                  showArrow={true}
                                  showSearch={true}
                                  size="large"
                                  filterOption={(input: any, option: any) =>
                                    String(option?.children ?? "")
                                      .toLowerCase()
                                      .indexOf(input.toLowerCase()) >= 0
                                  }
                                  placeholder="Select currency from here..."
                                >
                                  {/* {currencies?.map(
                                  ({ id, name, symbol }: ICurrency) => (
                                    <Option key={id} value={id}>
                                      {`${name} (${symbol})`}
                                    </Option>
                                  )
                                )} */}

                                  {item?.stock_type === "international" ? (
                                    <Option key={138} value={138}>
                                      USD ($)
                                    </Option>
                                  ) : (
                                    currencies?.map(
                                      ({ id, name, symbol }: ICurrency) => (
                                        <Option key={id} value={id}>
                                          {`${name} (${symbol})`}
                                        </Option>
                                      )
                                    )
                                  )}
                                </Select>
                              </div>
                              <div>
                                <label className="">Stock Value</label>
                                <Input
                                  className="custom-react-select borderless-input"
                                  size="large"
                                  contentEditable={
                                    item?.stock_type != "international"
                                  }
                                  disabled={
                                    item?.stock_type === "international"
                                  }
                                  value={item?.stock_value}
                                  onChange={e => {
                                    const updatedStockValue = e.target.value;
                                    setCompany(prev => ({
                                      ...prev,
                                      stock_data: prev.stock_data.map(
                                        (x, idx) =>
                                          idx === i
                                            ? {
                                                ...x,
                                                stock_value: updatedStockValue,
                                              }
                                            : x
                                      ),
                                    }));
                                  }}
                                  type="number"
                                  placeholder="Enter stock value here..."
                                />
                              </div>
                            </div>
                          );
                        })
                      : null}
                  </div>
                </Row>
              </Col>
            </Row>
          </div>

          {/* contract details */}
          <div className="basicInfo mb-10">
            <h2 className="text-center text-gray-700 font-medium text-lg mb-8">
              Contract Details
            </h2>

            <Row className="addCompany__row" gutter={[24, 24]}>
              <Col span={18}>
                <Row gutter={24}>
                  <Col span={12}>
                    <DatePicker
                      className="width-100"
                      value={
                        !company?.contract_start_date
                          ? undefined
                          : moment(company?.contract_start_date)
                      }
                      onChange={date => {
                        setCompany(prev => ({
                          ...prev,
                          contract_start_date: moment(date)
                            .format("YYYY-MM-DD HH:mm:ss")
                            .toString(),
                        }));
                        setIsDisabled(false);
                      }}
                      placeholder="Starting Date (DD/MM/YY)"
                    />
                  </Col>

                  <Col span={12}>
                    <DatePicker
                      disabled={isDisabled}
                      disabledDate={disabledDate}
                      className="width-100"
                      value={
                        !company?.contract_end_date
                          ? undefined
                          : moment(company?.contract_end_date)
                      }
                      onChange={date =>
                        setCompany(prev => ({
                          ...prev,
                          contract_end_date: moment(date)
                            .format("YYYY-MM-DD HH:mm:ss")
                            .toString(),
                        }))
                      }
                      placeholder="Ending Date (DD/MM/YY)"
                    />
                  </Col>
                </Row>
              </Col>

              <Col span={6} />
            </Row>
          </div>

          {/* contract details */}
          <div className="basicInfo mb-10">
            <h2 className="text-center text-gray-700 font-medium text-lg mb-8">
              Contact Person Details
            </h2>

            <Row className="addCompany__row" gutter={[24, 24]}>
              <Col span={18}>
                <Row gutter={[24, 24]}>
                  {/* Contact Person */}
                  <Col span={12}>
                    <label className="input__label">
                      Contact Person{" "}
                      <span className="text-red-500">
                        {company?.person_name ? "" : "*"}
                      </span>
                    </label>

                    <Input
                      className="custom-react-select borderless-input"
                      size="large"
                      name="person_name"
                      value={company?.person_name}
                      onChange={onChange}
                      placeholder="Enter name of contact person..."
                    />
                  </Col>

                  {/* Contact Person Country */}
                  <Col span={12}>
                    <label className="input__label">
                      Contact Person's Country{" "}
                      <span className="text-red-500">
                        {company?.person_country_id ? "" : "*"}
                      </span>
                    </label>

                    <Select
                      className="custom-react-select"
                      value={company?.person_country_id || undefined}
                      onChange={person_country_id =>
                        setCompany(prev => ({
                          ...prev,
                          person_country_id,
                        }))
                      }
                      showSearch
                      showArrow
                      size="large"
                      placeholder="Select country from here..."
                      filterOption={(input: any, option: any) =>
                        String(option?.children ?? "")
                          .toLowerCase()
                          .includes(input.toLowerCase())
                      }
                    >
                      {countries?.map(({ id, name }) => (
                        <Option key={id} value={id}>
                          {name}
                        </Option>
                      ))}
                    </Select>
                  </Col>

                  {/* Contact Number */}
                  <Col span={12}>
                    <label className="input__label">
                      Contact Number{" "}
                      <span className="text-red-500">
                        {company?.person_phone_code &&
                        company?.person_phone_number
                          ? ""
                          : "*"}
                      </span>
                    </label>

                    <ConfigProvider locale={en}>
                      <CountryPhoneInput
                        placeholder="Enter contact number here..."
                        onChange={e =>
                          setCompany(prev => ({
                            ...prev,
                            person_phone_code: e.code as number,
                            person_phone_number: e.phone as string,
                          }))
                        }
                        value={{
                          phone: company?.person_phone_number,
                          code: company?.person_phone_code || 92,
                        }}
                      />
                    </ConfigProvider>
                  </Col>

                  {/* Email */}
                  <Col span={12}>
                    <label className="input__label">
                      Email Address{" "}
                      <span className="text-red-500">
                        {company?.person_email ? "" : "*"}
                      </span>
                    </label>

                    <Input
                      className="custom-react-select borderless-input"
                      size="large"
                      name="person_email"
                      value={company?.person_email}
                      onChange={onChange}
                      placeholder="Enter email address here..."
                    />
                  </Col>
                </Row>
              </Col>

              <Col span={6} />
            </Row>
          </div>

          <Row className="addCompany__btns fixed bottom-5 w-full">
            <Col span={24} className="flex items-center justify-center">
              <CustomButton
                // type="primary"
                btnName={
                  isCreating || isUpdating ? (
                    <LoadingOutlined className="spinner" />
                  ) : (
                    `${isEdit ? "Update" : "Create"} Company`
                  )
                }
                disabledProp={validateValues(company) || !largeLogo}
                // size="large"
                onClick={onSubmit}
              >
                {isCreating || isUpdating ? (
                  <LoadingOutlined className="spinner" />
                ) : (
                  `${isEdit ? "Update" : "Create"} Company`
                )}
              </CustomButton>

              <CustomButton
                btnName={"Cancel"}
                // size="large"
                onClick={() =>
                  history.push(
                    paths.client.users.companies.profile.details.replace(
                      ":company_id",
                      company_id
                    )
                  )
                }
              >
                Cancel
              </CustomButton>
            </Col>
          </Row>
        </div>
      </div>
    </>
  );
};

export default AdminCreateCompany;
